import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { qcDecision } from '../../lib/callables';
import { LS_Snapshot, Annotation } from '../../types/labelSchema';
import { RejectReason } from '../../types/labeling';

interface LabelingJob {
  id: string;
  submissionId: string;
  taskId: string;
  mediaUrl: string;
  status: string;
  assigneeId: string;
  labelsFinal: Annotation[];
  labelSchemaSnapshot: LS_Snapshot;
  createdAt: any;
  updatedAt: any;
  classification?: {
    classId: string;
    source?: string;
    approvedAt?: any;
    approvedBy?: string;
  };
}

export default function QCReview() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'QC Review — Pixano';
  }, []);
  
  const [job, setJob] = useState<LabelingJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  
  // UI state
  const [showBoxes, setShowBoxes] = useState(true);
  const [showPolygons, setShowPolygons] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'approve' | 'needs_fix' | 'reject' | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [customReason, setCustomReason] = useState('');
  const [rejectReasons, setRejectReasons] = useState<RejectReason[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load job
        const jobDoc = await getDoc(doc(db, 'labelingJobs', jobId!));
        if (!jobDoc.exists()) {
          setError('Job not found');
          return;
        }
        const jobData = { id: jobDoc.id, ...jobDoc.data() } as LabelingJob;
        setJob(jobData);
        
        // Load reject reasons
        const reasonsQuery = query(
          collection(db, 'rejectReasons'),
          where('active', '==', true),
          orderBy('order')
        );
        const reasonsSnap = await getDocs(reasonsQuery);
        const reasonsData = reasonsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as RejectReason));
        setRejectReasons(reasonsData);
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data');
        setLoading(false);
      }
    };
    
    loadData();
  }, [jobId]);

  const handleReasonToggle = (reason: string) => {
    setReasons(prev => 
      prev.includes(reason) 
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
  };

  const handleAddCustomReason = () => {
    if (customReason.trim() && !reasons.includes(customReason.trim())) {
      setReasons(prev => [...prev, customReason.trim()]);
      setCustomReason('');
    }
  };

  const handleQCDecision = async (action: 'approve' | 'needs_fix' | 'reject') => {
    if ((action === 'needs_fix' || action === 'reject') && reasons.length === 0) {
      alert('Please provide at least one reason for this decision.');
      return;
    }

    try {
      setProcessing(true);
      await qcDecision(jobId!, action, reasons.length > 0 ? reasons : []);
      
      const actionText = action === 'approve' ? 'approved' : 
                        action === 'needs_fix' ? 'returned for fixes' : 'rejected';
      alert(`Job ${actionText} successfully!`);
      navigate('/labeling/queue');
    } catch (err) {
      console.error('Error making QC decision:', err);
      alert('Failed to process QC decision. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading QC review...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Job not found'}</p>
          <button
            onClick={() => navigate('/labeling/queue')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Queue
          </button>
        </div>
      </div>
    );
  }

  const schema = job.labelSchemaSnapshot;
  const annotations = job.labelsFinal || [];

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Detection Queue - QC Review</h1>
            <p className="text-sm text-gray-600">Queue Item {job.id.slice(-8)} • Annotator: {job.assigneeId}</p>
            
            {/* Context Chips */}
            <div className="flex items-center space-x-2 mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                Task: {job.taskId}
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                Type: Detection
              </span>
              {/* Classification chip if submission has classification */}
              {job.submissionId && job.classification && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                  Class: {job.classification.classId}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showBoxes}
                  onChange={(e) => setShowBoxes(e.target.checked)}
                  className="mr-2"
                />
                Show Boxes
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showPolygons}
                  onChange={(e) => setShowPolygons(e.target.checked)}
                  className="mr-2"
                />
                Show Polygons
              </label>
            </div>
            <button
              onClick={() => navigate('/labeling/queue')}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back to Queue
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Left - Image with Overlays */}
        <div className="flex-1 bg-gray-100 p-4">
          <div className="bg-white rounded-lg shadow-sm p-4 h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Annotation Review</h3>
              <div className="text-sm text-gray-600">
                {annotations.length} annotation{annotations.length !== 1 ? 's' : ''}
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="relative">
                <img
                  src={job.mediaUrl}
                  alt="Review image"
                  className="max-w-full max-h-full"
                  style={{ maxHeight: 'calc(100vh - 200px)' }}
                />
                
                {/* Render annotations */}
                {showBoxes && annotations.map((annotation, index) => {
                  const [x1, y1, x2, y2] = annotation.bbox;
                  const category = schema.categories.find(c => c.id === annotation.categoryId);
                  
                  return (
                    <div
                      key={index}
                      className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20"
                      style={{
                        left: x1,
                        top: y1,
                        width: x2 - x1,
                        height: y2 - y1,
                        pointerEvents: 'none'
                      }}
                    >
                      <div className="absolute -top-6 left-0 bg-blue-500 text-white text-xs px-1 rounded">
                        {category?.label || annotation.categoryId} ({index + 1})
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right - QC Actions */}
        <div className="w-96 bg-white border-l border-gray-200 p-4">
          <h3 className="font-semibold mb-4">QC Decision</h3>
          
          {/* Action Buttons */}
          <div className="space-y-3 mb-6">
            <button
              onClick={() => setSelectedAction('approve')}
              className={`w-full px-4 py-3 rounded-lg border-2 ${
                selectedAction === 'approve'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 hover:border-green-300'
              }`}
            >
              <div className="font-medium">✅ Approve</div>
              <div className="text-sm text-gray-600">Accept annotations as-is</div>
            </button>
            
            <button
              onClick={() => setSelectedAction('needs_fix')}
              className={`w-full px-4 py-3 rounded-lg border-2 ${
                selectedAction === 'needs_fix'
                  ? 'border-orange-500 bg-orange-50 text-orange-700'
                  : 'border-gray-200 hover:border-orange-300'
              }`}
            >
              <div className="font-medium">🔧 Needs Fix</div>
              <div className="text-sm text-gray-600">Return to annotator with feedback</div>
            </button>
            
            <button
              onClick={() => setSelectedAction('reject')}
              className={`w-full px-4 py-3 rounded-lg border-2 ${
                selectedAction === 'reject'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-200 hover:border-red-300'
              }`}
            >
              <div className="font-medium">❌ Reject</div>
              <div className="text-sm text-gray-600">Reject annotations entirely</div>
            </button>
          </div>

          {/* Reasons Section */}
          {(selectedAction === 'needs_fix' || selectedAction === 'reject') && (
            <div className="mb-6">
              <h4 className="font-medium mb-3">Reasons (Required)</h4>
              
              <div className="space-y-2 mb-4">
                {rejectReasons.map(reason => (
                  <label key={reason.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={reasons.includes(reason.label)}
                      onChange={() => handleReasonToggle(reason.label)}
                      className="mr-2"
                    />
                    <span className="text-sm">{reason.label}</span>
                  </label>
                ))}
              </div>
              
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Add custom reason..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleAddCustomReason}
                  disabled={!customReason.trim()}
                  className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
              
              {reasons.length > 0 && (
                <div className="mt-3">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Selected Reasons:</h5>
                  <div className="space-y-1">
                    {reasons.map(reason => (
                      <div key={reason} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded">
                        <span className="text-sm">{reason}</span>
                        <button
                          onClick={() => handleReasonToggle(reason)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          {selectedAction && (
            <button
              onClick={() => handleQCDecision(selectedAction)}
              disabled={processing || ((selectedAction === 'needs_fix' || selectedAction === 'reject') && reasons.length === 0)}
              className={`w-full px-4 py-3 rounded-lg font-medium ${
                selectedAction === 'approve'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : selectedAction === 'needs_fix'
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {processing ? 'Processing...' : `Submit ${selectedAction === 'approve' ? 'Approval' : selectedAction === 'needs_fix' ? 'Needs Fix' : 'Rejection'}`}
            </button>
          )}

          {/* Schema Info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h4 className="font-medium mb-2">Schema Information</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Schema:</strong> {schema.name}</p>
              <p><strong>Version:</strong> {schema._ref?.version}</p>
              <p><strong>Categories:</strong> {schema.categories.length}</p>
              <p><strong>Hash:</strong> {schema._schemaHash?.slice(0, 8)}...</p>
            </div>
          </div>

          {/* Annotation Details */}
          <div className="mt-6">
            <h4 className="font-medium mb-2">Annotation Details</h4>
            <div className="space-y-2">
              {annotations.map((annotation, index) => {
                const category = schema.categories.find(c => c.id === annotation.categoryId);
                return (
                  <div key={index} className="bg-gray-50 p-2 rounded text-sm">
                    <div className="font-medium">{category?.label || annotation.categoryId}</div>
                    <div className="text-gray-600">
                      Box: [{annotation.bbox.map(Math.round).join(', ')}]
                    </div>
                    {annotation.attributes && Object.keys(annotation.attributes).length > 0 && (
                      <div className="text-gray-600">
                        Attributes: {Object.entries(annotation.attributes).map(([key, value]) => `${key}: ${value}`).join(', ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}